import React, { useCallback, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  change,
  Field,
  formValueSelector,
  InjectedFormProps,
  reduxForm,
} from "redux-form";
import * as ReactBootstrap from "react-bootstrap";
import SlidingPane from "react-sliding-pane";

import {
  FormButtons,
  ICONS,
  renderers,
  validations,
} from "../../../components";
import { GSXPart, PartDataTable } from "../../../GSXComponents";
import { TicketPrint } from "../../../api";
import { calculateFinalPrice } from "./utils";

const ESTIMATION_FORM_NAME = "estimation-form";

// Props passed from parent component
type PartRequestFormHTMLProps = {
  ticket?: TicketPrint;
  selectedComponentCode?: string;
  isIntangiblePart?: boolean;
  isQuote?: boolean;
};

// Props for connected + reduxForm component
type EstimationLineFormProps = PartRequestFormHTMLProps &
  InjectedFormProps<{}, PartRequestFormHTMLProps> & {
    finalPrice: string;
    stock_price: string | number;
    margin: number;
    gst: number;
  };

const EstimationLineFormHTML: React.FC<EstimationLineFormProps> = ({
  ticket,
  handleSubmit,
  pristine,
  reset,
  submitting,
  stock_price,
  margin,
  gst,
}) => {
  const dispatch = useDispatch();
  const [isPaneOpen, setIsPaneOpen] = useState(false);

  const setField = useCallback(
    (field: string, value: any) => {
      dispatch(change(ESTIMATION_FORM_NAME, field, value));
    },
    [dispatch]
  );

  const handlePartSelection = (part: GSXPart) => {
    setField("description", part.description);
    setField("part_number", part.number);
    setField("stock_price", (part.stockPrice || "").replace(/,/g, ""));
    setIsPaneOpen(false);
  };

  const recalculate = useCallback(() => {
    const finalPrice = calculateFinalPrice(stock_price, gst, margin);
    setField("final_amount", finalPrice);
  }, [stock_price, gst, margin, setField]);

  return (
    <form onSubmit={handleSubmit}>
      <ReactBootstrap.Row>
        <ReactBootstrap.Col xs="12">
          <Field
            name="description"
            label="Description"
            validate={[validations.required]}
            component={renderers.renderField}
            type="text"
          />
          <div className="text-end mb-3">
            <ReactBootstrap.Button
              onClick={() => setIsPaneOpen(true)}
              variant="dark"
              size="sm"
            >
              {ICONS.SEARCH} Search Parts
            </ReactBootstrap.Button>
          </div>
        </ReactBootstrap.Col>

        <SlidingPane
          isOpen={isPaneOpen}
          title="Select the Part"
          overlayClassName="on-top"
          onRequestClose={() => setIsPaneOpen(false)}
        >
          {ticket && (
            <PartDataTable
              onSelection={handlePartSelection}
              device_identifier={ticket.device.identifier}
              extraItems={["Service Charge"]}
            />
          )}
        </SlidingPane>

        <ReactBootstrap.Col xs="12">
          <Field
            name="part_number"
            label="Part Number"
            validate={[validations.required]}
            component={renderers.renderField}
            type="text"
          />
        </ReactBootstrap.Col>

        <ReactBootstrap.Col xs="12">
          <Field
            name="stock_price"
            label="Stock Price"
            validate={[validations.required]}
            component={renderers.renderField}
            type="text"
            onBlur={recalculate}
          />
        </ReactBootstrap.Col>
      </ReactBootstrap.Row>

      <ReactBootstrap.Row>
        <ReactBootstrap.Col xs={4}>
          <Field
            name="gst"
            label="GST %"
            validate={[validations.required]}
            component={renderers.renderField}
            type="number"
            onBlur={recalculate}
          />
        </ReactBootstrap.Col>
        <ReactBootstrap.Col xs={4}>
          <Field
            name="margin"
            label="Margin %"
            validate={[validations.required]}
            component={renderers.renderField}
            type="number"
            onBlur={recalculate}
          />
        </ReactBootstrap.Col>
        <ReactBootstrap.Col xs={4}>
          <Field
            name="final_amount"
            label="Final Amount"
            validate={[validations.required]}
            component={renderers.renderField}
            type="number"
            disabled
          />
        </ReactBootstrap.Col>
      </ReactBootstrap.Row>

      <FormButtons submitting={submitting} pristine={pristine} reset={reset} />
    </form>
  );
};

const EstimationLineFormRaw = reduxForm<{}, PartRequestFormHTMLProps>({
  form: ESTIMATION_FORM_NAME,
  initialValues: {
    gst: 18,
    margin: 0,
  },
})(EstimationLineFormHTML);

const selector = formValueSelector(ESTIMATION_FORM_NAME);

export const EstimationLineForm = connect((state) => {
  const stock_price = selector(state, "stock_price");
  const margin = selector(state, "margin");
  const gst = selector(state, "gst");
  const finalPrice = calculateFinalPrice(stock_price, gst, margin);

  return {
    stock_price,
    margin,
    gst,
    finalPrice,
  };
})(EstimationLineFormRaw);
